<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Support" icon="fa-messages-question"/>

            <div class="content mt-4">
                <p class="has-text-white" v-if="!isHelpView && supportLoading && !supportTickets.length && !selectedTicket"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading tickets...</p>

                <!-- Ticket Detail View -->
                <div v-else-if="selectedTicket">
                    <div class="mb-5">
                        <a @click="backToList" class="back-link"><i class="far fa-arrow-left mr-2"></i>Back to Tickets</a>
                    </div>

                    <div class="ticket-panel">
                        <div class="ticket-detail-header mb-5">
                            <div class="columns is-mobile is-vcentered">
                                <div class="column">
                                    <h3 class="ticket-subject">{{ selectedTicket.subject }}</h3>
                                    <div class="ticket-meta mt-1">Opened {{ formatDate(selectedTicket.created_at) }}</div>
                                    <div class="ticket-contact mt-2" v-if="selectedTicket.email_from || selectedTicket.phone_from">
                                        <span v-if="selectedTicket.email_from"><i class="far fa-envelope mr-1"></i>{{ selectedTicket.email_from }}</span>
                                        <span v-if="selectedTicket.email_from && selectedTicket.phone_from" class="mx-2">|</span>
                                        <span v-if="selectedTicket.phone_from"><i class="far fa-phone mr-1"></i>{{ selectedTicket.phone_from }}</span>
                                    </div>
                                </div>
                                <div class="column is-narrow has-text-right">
                                    <b-tag :type="statusType(selectedTicket.status)" size="is-medium">{{ statusLabel(selectedTicket.status) }}</b-tag>
                                    <div class="channel-label mt-2">
                                        <i class="far" :class="channelIcon(selectedTicket.channel_type)"></i> {{ channelLabel(selectedTicket.channel_type) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="messages-thread">
                            <div v-for="(msg, index) in selectedTicket.support_messages" :key="msg.id"
                                 class="message-item" :class="{'is-user': msg.is_inbound, 'is-support': !msg.is_inbound}">
                                <div class="columns is-gapless">
                                    <div class="column is-narrow mr-4">
                                        <div class="avatar" :class="{'user-avatar': msg.is_inbound, 'support-avatar': !msg.is_inbound}">
                                            <i :class="msg.is_inbound ? 'fas fa-user' : 'fas fa-messages-question'"></i>
                                        </div>
                                    </div>
                                    <div class="column">
                                        <div class="msg-info">
                                            <strong>{{ msg.is_inbound ? 'You' : formatSender(selectedTicket) }}</strong>
                                            <span class="message-date">{{ formatDate(msg.created_at) }}</span>
                                        </div>
                                        <iframe v-if="msg.body_html" class="message-iframe" :srcdoc="msg.body_html" sandbox="allow-same-origin" frameborder="0" @load="resizeIframe($event)"></iframe>
                                        <div class="message-body" v-else>{{ msg.body_text }}</div>
                                        <div class="message-attachments" v-if="msg.support_attachments && msg.support_attachments.length">
                                            <a v-for="att in msg.support_attachments" :key="att.id" :href="spacesUrl + '/' + att.storage_key" target="_blank" class="attachment">
                                                <i class="far fa-paperclip mr-1"></i>{{ att.filename }} <span class="att-size">({{ formatSize(att.size) }})</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <hr v-if="index < selectedTicket.support_messages.length - 1"/>
                            </div>
                        </div>

                        <div class="reply-section">
                            <p v-if="selectedTicket.status === 'closed'" class="reopen-notice mb-3">
                                <i class="far fa-info-circle mr-2"></i>This ticket is closed. Sending a reply will reopen it.
                            </p>
                            <h4 class="mb-3">Reply</h4>
                            <b-field>
                                <b-input v-model="replyText" type="textarea" placeholder="Type your reply..." rows="3"></b-input>
                            </b-field>
                            <b-button type="is-primary" @click="submitReply" :loading="sending" :disabled="!replyText.trim()">
                                <i class="far fa-paper-plane mr-2"></i>Send Reply
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- New Ticket Form -->
                <div v-else-if="showNewForm">
                    <div class="mb-5">
                        <a @click="cancelNewTicket" class="back-link"><i class="far fa-arrow-left mr-2"></i>Back</a>
                    </div>

                    <h1 class="has-text-white mt-2 mb-2">New Support Ticket</h1>

                    <div class="ticket-panel">
                        <h4 class="mb-4">How can we help?</h4>

                        <b-field label="Subject">
                            <b-input v-model="newTicket.subject" placeholder="Brief description of your issue"></b-input>
                        </b-field>

                        <b-field label="Message" class="mt-4">
                            <b-input v-model="newTicket.body_text" type="textarea" placeholder="Describe your issue in detail..." rows="6"></b-input>
                        </b-field>

                        <!-- Help Center answers matching what they've typed so far -->
                        <div class="suggestions mt-5" v-if="suggestions.length">
                            <div class="suggestions-heading"><i class="fas fa-lightbulb-on mr-2"></i>These Answers Might Help</div>
                            <div class="suggestions-list">
                                <help-article v-for="article in suggestions" :key="article.id" :article="article" :show-contact="false" :linkable="false"/>
                            </div>
                        </div>

                        <hr/>

                        <b-button type="is-primary" size="is-medium" @click="submitNewTicket" :loading="sending"
                                  :disabled="!newTicket.subject.trim() || !newTicket.body_text.trim()">
                            <i class="far fa-paper-plane mr-2"></i>Submit Ticket
                        </b-button>
                    </div>
                </div>

                <help-center v-else-if="isHelpView" :view="view" :item="item"/>

                <!-- Ticket List View (/support/tickets) -->
                <div v-else>
                    <div class="columns pt-6">
                        <div class="column">
                            <div class="columns is-mobile is-vcentered mb-2">
                                <div class="column">
                                    <h2 class="list-heading mb-0"><i class="fad fa-messages-question fa-fw mr-3"></i>Your Tickets</h2>
                                </div>
                            </div>

                            <div v-if="!supportLoading && supportTickets.length === 0" class="empty-state mt-5">
                                <div class="columns">
                                    <div class="column is-narrow is-hidden-touch">
                                        <i class="fad fa-ticket fa-3x" style="color: #9faec2;"></i>
                                    </div>
                                    <div class="column">
                                        <p class="has-text-weight-bold">No support tickets yet</p>
                                        <p class="meta">Have a question? Many answers are in the <router-link :to="{name: 'support'}">Help Center</router-link>, or <router-link :to="{name: 'support', params: {view: 'new'}}">send us a message</router-link> and our team will get back to you.</p>
                                    </div>
                                </div>
                            </div>

                            <div v-for="ticket in supportTickets" :key="ticket.id"
                                 class="ticket-card mb-4" @click="showTicket(ticket.id)">
                                <div class="information">
                                    <div class="columns is-mobile">
                                        <div class="column is-narrow is-hidden-touch">
                                            <i class="fad fa-ticket has-text-primary fa-2x"></i>
                                        </div>
                                        <div class="column">
                                            <div class="columns is-block-tablet is-flex-widescreen is-vcentered">
                                                <div class="column">
                                                    <div class="has-text-weight-bold">
                                                        <i class="fad fa-ticket has-text-primary is-hidden-desktop mr-2"></i>
                                                        {{ ticket.subject }}
                                                    </div>
                                                    <div class="meta">{{ formatDate(ticket.last_message_at || ticket.created_at) }}</div>
                                                    <div class="meta" v-if="ticket._count">{{ ticket._count.support_messages }} message{{ ticket._count.support_messages !== 1 ? 's' : '' }}</div>
                                                </div>
                                                <div class="column is-narrow has-text-right">
                                                    <b-tag :type="statusType(ticket.status)">{{ statusLabel(ticket.status) }}</b-tag>
                                                    <div class="meta mt-1"><i class="far mr-1" :class="channelIcon(ticket.channel_type)"></i>{{ channelLabel(ticket.channel_type) }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import {mapGetters, mapActions} from "vuex";
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import HelpCenter from "../../components/support/HelpCenter";
    import HelpArticle from "../../components/support/HelpArticle";
    import {searchHelp} from "../../utils/helpSearch";

    // Help Center views; everything else is tickets
    const HELP_VIEWS = ['', 'topic', 'help'];

    export default {
        components: {Framework, SidePanelNav, MainHeader, HelpCenter, HelpArticle},
        props: {
            // Route params (/support/:view/:item):
            //   '' Help Center home | 'topic'/:key | 'help'/:articleId
            //   'tickets' your tickets | 'new' new ticket form
            view: {type: String, default: ''},
            item: {type: String, default: null}
        },
        data() {
            return {
                selectedTicket: null,
                showNewForm: false,
                sending: false,
                replyText: '',
                newTicket: {
                    subject: '',
                    body_text: ''
                },
                spacesUrl: 'https://my247ed.sfo3.cdn.digitaloceanspaces.com',
                suggestText: '',
                suggestTimer: null
            };
        },
        computed: {
            ...mapGetters(['supportTickets', 'currentTicket', 'supportLoading']),
            isHelpView() {
                return HELP_VIEWS.includes(this.view || '');
            },
            suggestions() {
                return searchHelp(this.suggestText, {limit: 3});
            }
        },
        methods: {
            ...mapActions(['loadTickets', 'loadTicket', 'createTicket', 'sendReply']),
            // Tickets are routable (/support/tickets/:id) so the reply e-mail
            // can link straight to the conversation
            showTicket(id) {
                this.$router.push({name: 'support', params: {view: 'tickets', item: String(id)}}).catch(() => {});
            },
            openTicket(id) {
                this.loadTicket(id).then(() => {
                    this.selectedTicket = this.currentTicket;
                    this.replyText = '';
                }).catch(() => {
                    this.$notify('error', 'Ticket Unavailable', "We couldn't open that support ticket.");
                    this.$router.replace({name: 'support', params: {view: 'tickets'}}).catch(() => {});
                });
            },
            backToList() {
                this.$router.push({name: 'support', params: {view: 'tickets'}}).catch(() => {});
                this.loadTickets();
            },
            // The new-ticket form is routable (/support/new): the side panel
            // opens it, and Back goes through the router too
            cancelNewTicket() {
                if (window.history.length > 1) this.$router.back();
                else this.$router.push({name: 'support'}).catch(() => {});
            },
            submitNewTicket() {
                this.sending = true;
                this.createTicket({
                    subject: this.newTicket.subject,
                    body_text: this.newTicket.body_text
                }).then((result) => {
                    this.newTicket = {subject: '', body_text: ''};
                    this.$router.replace({name: 'support', params: {view: 'tickets', item: String(result.id)}}).catch(() => {});
                }).finally(() => {
                    this.sending = false;
                });
            },
            submitReply() {
                if (!this.replyText.trim()) return;
                this.sending = true;
                this.sendReply({
                    id: this.selectedTicket.id,
                    data: {body_text: this.replyText}
                }).then(() => {
                    this.replyText = '';
                    this.selectedTicket = this.currentTicket;
                }).finally(() => {
                    this.sending = false;
                });
            },
            statusType(status) {
                return status === 'closed' ? 'is-light' : 'is-success';
            },
            statusLabel(status) {
                return status === 'closed' ? 'Closed' : 'Open';
            },
            channelLabel(type) {
                switch (type) {
                    case 'email': return 'E-Mail';
                    case 'sms': return 'SMS';
                    case 'direct': return 'Direct';
                    default: return type || 'Direct';
                }
            },
            channelIcon(type) {
                switch (type) {
                    case 'email': return 'fa-envelope';
                    case 'sms': return 'fa-comment-sms';
                    case 'direct': return 'fa-message';
                    default: return 'fa-message';
                }
            },
            formatDate(dateStr) {
                if (!dateStr) return '';
                let d = new Date(dateStr);
                return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'});
            },
            formatSender(ticket) {
                if (ticket?.support_channels) {
                    return ticket.support_channels.display_name || ticket.support_channels.name || 'Support Team';
                }
                return 'Support Team';
            },
            formatSize(bytes) {
                if (!bytes) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / 1048576).toFixed(1) + ' MB';
            },
            resizeIframe(e) {
                let iframe = e.target;
                try {
                    let doc = iframe.contentDocument || iframe.contentWindow.document;
                    let style = doc.createElement('style');
                    style.textContent = `
                        html, body { margin: 0; padding: 0; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; font-size: 0.85rem; line-height: 1.5; color: #333; word-break: break-word; }
                        img { max-width: 100%; height: auto; }
                        .quote-collapsed { display: none; }
                        .quote-toggle { display: inline-block; cursor: pointer; color: #336699; font-size: 0.8rem; padding: 2px 8px; margin: 4px 0; border: 1px solid #ccc; border-radius: 3px; background: #f5f5f5; user-select: none; }
                        .quote-toggle:hover { background: #e8e8e8; }
                    `;
                    doc.head.appendChild(style);

                    // Collapse quoted content
                    this.collapseQuotes(doc);

                    setTimeout(() => {
                        iframe.style.height = doc.body.scrollHeight + 'px';
                    }, 0);
                } catch (err) {}
            },
            collapseQuotes(doc) {
                // Wrap known quote elements
                let quoteSelectors = [
                    '.gmail_quote',
                    '.gmail_extra',
                    '.moz-cite-prefix + blockquote',
                    '.yahoo_quoted',
                    'blockquote[type="cite"]',
                    '#appendonsend + hr ~ *',
                ];

                let found = false;
                quoteSelectors.forEach(sel => {
                    doc.querySelectorAll(sel).forEach(el => {
                        if (!el.classList.contains('quote-collapsed')) {
                            el.classList.add('quote-collapsed');
                            found = true;
                        }
                    });
                });

                // Detect "On ... wrote:" or "---------- Original Message ----------" in text nodes
                let body = doc.body;
                if (!found && body) {
                    let html = body.innerHTML;
                    let patterns = [
                        /(<br\s*\/?>)\s*(On\s.+wrote:\s*<br)/i,
                        /(<br\s*\/?>)\s*(-{2,}\s*Original Message\s*-{2,})/i,
                        /(<br\s*\/?>)\s*(-{2,}\s*Forwarded message\s*-{2,})/i,
                        /(<br\s*\/?>)\s*(From:\s.+<br)/i,
                    ];
                    for (let pat of patterns) {
                        let match = html.match(pat);
                        if (match && match.index !== undefined) {
                            let splitAt = match.index + match[1].length;
                            let before = html.substring(0, splitAt);
                            let after = html.substring(splitAt);
                            body.innerHTML = before + '<div class="quote-collapsed">' + after + '</div>';
                            found = true;
                            break;
                        }
                    }
                }

                // Also handle plain blockquotes that take up most of the content
                if (!found) {
                    let blockquotes = doc.querySelectorAll('blockquote');
                    blockquotes.forEach(bq => {
                        if (bq.textContent.length > 100) {
                            bq.classList.add('quote-collapsed');
                            found = true;
                        }
                    });
                }

                // Add toggle button if we collapsed anything
                if (found) {
                    let collapsed = doc.querySelectorAll('.quote-collapsed');
                    if (collapsed.length > 0) {
                        let toggle = doc.createElement('span');
                        toggle.className = 'quote-toggle';
                        toggle.textContent = '•••';
                        toggle.title = 'Show quoted text';
                        let iframe = doc.defaultView.frameElement;
                        toggle.addEventListener('click', () => {
                            collapsed.forEach(el => el.classList.toggle('quote-collapsed'));
                            toggle.title = collapsed[0].classList.contains('quote-collapsed') ? 'Show quoted text' : 'Hide quoted text';
                            // Resize iframe after toggle
                            setTimeout(() => {
                                iframe.style.height = doc.body.scrollHeight + 'px';
                            }, 0);
                        });
                        collapsed[0].parentNode.insertBefore(toggle, collapsed[0]);
                    }
                }
            }
        },
        watch: {
            view: {
                immediate: true,
                handler(v) {
                    this.showNewForm = (v === 'new');
                    if (v !== 'tickets') this.selectedTicket = null;
                }
            },
            // /support/tickets/:id opens that ticket; bare /support/tickets the list
            item: {
                immediate: true,
                handler(id) {
                    if (this.view !== 'tickets') return;
                    if (id && /^\d+$/.test(id)) this.openTicket(Number(id));
                    else this.selectedTicket = null;
                }
            },
            // Suggestions follow the subject + message, a beat after typing stops
            newTicket: {
                deep: true,
                handler(t) {
                    clearTimeout(this.suggestTimer);
                    this.suggestTimer = setTimeout(() => {
                        this.suggestText = (t.subject + ' ' + t.body_text).trim();
                    }, 400);
                }
            }
        },
        beforeDestroy() {
            clearTimeout(this.suggestTimer);
        },
        mounted() {
            this.loadTickets();
        }
    };
</script>

<style lang="scss" scoped>
    .list-heading {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    .back-link {
        color: #9faec2;
        font-size: 0.9rem;

        &:hover {
            color: white;
        }
    }

    .ticket-card {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        cursor: pointer;
        transition: transform 150ms;

        &:hover {
            transform: translateY(-1px);
        }

        .information {
            padding: 1.5rem;
        }

        .meta {
            font-weight: normal;
            font-size: 0.8rem;
            color: #666d7a;
        }
    }

    .empty-state {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 2.5rem;

        p {
            color: #4a4a4a;
        }

        .meta {
            font-size: 0.9rem;
            color: #666d7a;
            margin-top: 0.25rem;
        }
    }

    .ticket-panel {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 3rem 3rem 4rem;
        margin-top: 2rem;
        margin-bottom: 6rem;

        @media (max-width: 1180px) {
            padding: 2rem;
        }

        @media (max-width: 768px) {
            padding: 1rem;
        }
    }

    .ticket-detail-header {
        border-bottom: 2px solid #e0e2e8;
        padding-bottom: 1.25rem;

        .ticket-subject {
            font-size: 1.3rem;
            font-weight: bold;
            color: #224C74;
            margin: 0;
        }

        .ticket-meta {
            font-size: 0.8rem;
            color: #666d7a;
        }

        .ticket-contact {
            font-size: 0.85rem;
            color: #555;

            i {
                color: #999;
            }
        }

        .channel-label {
            font-size: 0.8rem;
            color: #666d7a;
        }
    }

    .messages-thread {
        .message-item {
            padding: 0.5rem 0;

            hr {
                margin: 1.5rem 0;
                background-color: #e0e2e8;
                height: 1px;
            }

            .avatar {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.85rem;
                color: white;
                margin-top: 2px;

                &.user-avatar {
                    background-color: #336699;
                }

                &.support-avatar {
                    background-color: #48c774;
                }
            }

            .msg-info {
                strong {
                    color: #363636;
                }

                .message-date {
                    color: #999;
                    font-size: 0.8rem;
                    margin-left: 0.75rem;
                }
            }

            .message-iframe {
                width: 100%;
                min-height: 40px;
                border: none;
                overflow: hidden;
                margin-top: 0.35rem;
            }

            .message-body {
                color: #4a4a4a;
                line-height: 1.6;
                margin-top: 0.35rem;
                white-space: pre-wrap;
            }

            .message-attachments {
                margin-top: 0.5rem;
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;

                .attachment {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.7rem;
                    background: #f5f7fa;
                    border: 1px solid #e0e2e8;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    color: #336699;
                    text-decoration: none;

                    &:hover {
                        background: #edf0f5;
                        border-color: #ccc;
                    }

                    .att-size {
                        color: #999;
                        margin-left: 0.25rem;
                    }
                }
            }
        }
    }

    .reply-section {
        border-top: 2px solid #e0e2e8;
        margin-top: 3rem;
        padding-top: 2rem;

        h4 {
            color: #224C74;
            font-weight: bold;
        }
    }

    .suggestions {
        border: 1px solid #e6eaef;
        border-radius: 4px;
        overflow: hidden;

        .suggestions-heading {
            background: #f4f6f9;
            border-bottom: 1px solid #e6eaef;
            padding: 0.6rem 1.25rem;
            font-size: 0.85rem;
            font-weight: 600;
            color: #224C74;

            i {
                color: #e0a800;
            }
        }
    }

    .reopen-notice {
        color: #946c00;
        background-color: #fff8e1;
        border: 1px solid #ffe082;
        border-radius: 4px;
        padding: 0.75rem 1rem;
        font-size: 0.9rem;
    }
</style>
